import type {VNode} from 'snabbdom';

import {ModifierKeys} from 'common/util/keyboard';
import {ContextPanelData} from 'common/widgets/context-panel/types';

import {ButtonLayout, ButtonSize} from '../button/constants';
import {Directions} from '../drop-menu/types';
import {ItemsMenuTheme, Section, SectionItem} from '../items-menu/types';
import {TooltipAttrs, TooltipParams} from '../tooltip/types';
import {SelectTheme} from './constants';

type Renderer = () => VNode | null;

export interface SelectDropdownState {
  isOpen: boolean;
  isSearchable: boolean;
  searchFilter: string;
  sectionsFromInputFn: (input: string) => Section[];
  contextPanelTransform: (item: any, isExpanded?: boolean) => ContextPanelData;
  sections: Section[];
  dropdownBodyRenderer: Renderer;
  tooltipData: ContextPanelData; // tooltip info for currently focused item
}

export enum SelectOpenMode {
  Click = `click`,
  Hover = `hover`,
}

export type SelectAttrs = {
  'hide-input-focus-styling'?: boolean;

  'clear-search-on-change'?: boolean;

  'force-visibility'?: boolean;

  options?: string[];

  'empty-message'?: string;

  open?: boolean;

  'open-mode'?: SelectOpenMode;

  'preserve-search-on-close'?: boolean;

  sections?: Section[];

  'selected-option'?: string;

  'shortcut-label'?: string;

  label?: string;

  title?: string;

  'label-transform'?: TODO;

  multiple?: boolean;

  'multiple-save-button'?: boolean;

  'show-select-all'?: boolean;

  'search-placeholder'?: string;

  'disable-search'?: boolean;

  'search-threshold'?: number;

  'header-title'?: string;

  'back-button'?: boolean;

  'menu-width'?: number | string;

  // null means height 100%
  'menu-height'?: number | null;

  'menu-alignment'?: Directions;

  icon?: SelectIcon;

  'disable-tooltip'?: boolean;

  tooltip?: TooltipAttrs;

  theme?: SelectTheme;

  size?: ButtonSize;

  disabled?: boolean;

  'disabled-stays-opaque'?: boolean;

  readonly?: boolean;

  'icon-only'?: boolean;

  'no-button-truncate'?: boolean;

  'show-icon-only-down-caret'?: boolean;

  'always-show-caret'?: boolean;

  'never-show-caret'?: boolean;

  'track-name'?: string;

  'align-all-text'?: boolean;

  'use-fixed-dropmenu-pos'?: boolean;

  'show-preview-tooltip'?: boolean;

  'no-rounded-corners'?: boolean;

  'disable-focus'?: boolean;

  'prevent-close'?: boolean;

  'disable-close-on-select'?: boolean;

  layout?: ButtonLayout;

  'disable-first-item-padding'?: boolean;

  offset?: number;

  'list-theme'?: ItemsMenuTheme;

  'show-section-label-matches'?: boolean;

  'auto-focus'?: boolean;

  /**
   * @todo [pablo]
   * When set, makes the dropdown render along the document.body tag
   * and not within the DOM location of the mp-select element, before
   * this becomes the default behavior, we need further testing to remove
   * slots from mp-select
   */
  'experimental-positioning'?: boolean;

  // style overrides
  'style-override'?: string;
};

export type SelectParams = {
  clearClickCallback?: () => void;
  disablePressedStyles?: boolean;
  sections?: Section[];
  showClearButton?: boolean;
  tooltip?: TooltipAttrs & TooltipParams;
};

export interface SelectIcon {
  /** The icon that should appear to the left of the selected-option */
  leftIcon?: string;
  leftIconColor?: string;

  /** The icon that should appear to the right of the selected-option */
  rightIcon?: string;
  rightIconColor?: string;
}

export type MenuChangeEvent = CustomEvent<{
  isOpen: boolean;
}>;

export type ChangeEvent<T = any, ExtendWithT = Obj> = CustomEvent<{
  value: string;
  item: SectionItem<T, ExtendWithT>;
  parentItems?: SectionItem[];
  modifierKeys: ModifierKeys;
}>;

export type FocusItemEvent<T = any> = CustomEvent<{
  value: string;
  item: SectionItem<T>;
}>;

export type SearchFilterChangeEvent = CustomEvent<{
  searchFilter: string;
}>;

export type SelectElProps = {
  attrs?: SelectAttrs;
  params?: SelectParams;
  props?: {
    sections?: Section[];
    sectionsFn?: () => Section[];
    sectionsFromInputFn?: (input: string) => Section[];
    dropdownPositionParent?: HTMLElement;
    dropdownBodyRenderer?: Renderer;
    contextPanelPositionParentElement?: HTMLElement;
    contextPanelTransform?: (item: any, isExpanded?: boolean) => ContextPanelData;
  };
  on?: {
    change?: (ev: ChangeEvent) => void;
    open?: (ev: CustomEvent) => void;
    close?: (ev: CustomEvent) => void;
    menuChange?: (ev: MenuChangeEvent) => void;
    searchFilterChange?: (ev: SearchFilterChangeEvent) => void;
    focusItem?: (ev: FocusItemEvent) => void;
    backClick?: (ev: CustomEvent) => void;
  };
};
